// ============================================
// TV Channels Configuration - Type-Safe
// Each channel has a unique ID that maps to the streaming source
// ============================================

import type { Channel, ChannelCategory, ChannelsByCategory } from '../types/channel';

// React Icons imports - using Simple Icons (si) for brand logos and other packs
import {
    SiWwe,
    SiHbo,
    SiNbc
} from 'react-icons/si';
import {
    BiFootball
} from 'react-icons/bi';
import {
    MdSportsSoccer,
    MdChildCare,
    MdStar,
    MdTheaters,
    MdLocalMovies,
    MdPublic
} from 'react-icons/md';
import {
    FaNewspaper,
    FaTv,
    FaChild,
    FaGlobeAmericas
} from 'react-icons/fa';
import {
    GiRaceCar,
    GiCastle
} from 'react-icons/gi';
import {
    IoMusicalNotes
} from 'react-icons/io5';

export const TV_CHANNELS: readonly Channel[] = [
    // Sports
    {
        id: 'stream-38',
        name: 'Sky Sports',
        category: 'Sports' as ChannelCategory,
        logo: MdSportsSoccer,
        color: 'from-red-600 to-blue-600'
    },
    {
        id: 'stream-414',
        name: 'SuperSport',
        category: 'Sports' as ChannelCategory,
        logo: BiFootball,
        color: 'from-green-600 to-yellow-500'
    },
    {
        id: 'stream-273',
        name: 'Formula 1',
        category: 'Sports' as ChannelCategory,
        logo: GiRaceCar,
        color: 'from-red-600 to-black'
    },
    {
        id: 'stream-376',
        name: 'WWE',
        category: 'Sports' as ChannelCategory,
        logo: SiWwe,
        color: 'from-red-700 to-yellow-500'
    },

    // News
    {
        id: 'stream-345',
        name: 'CNN',
        category: 'News' as ChannelCategory,
        logo: FaTv,
        color: 'from-red-700 to-red-900'
    },
    {
        id: 'stream-347',
        name: 'Fox News',
        category: 'News' as ChannelCategory,
        logo: FaNewspaper,
        color: 'from-blue-800 to-red-600'
    },

    // Entertainment
    {
        id: 'stream-321',
        name: 'HBO',
        category: 'Entertainment' as ChannelCategory,
        logo: SiHbo,
        color: 'from-black to-purple-900'
    },
    {
        id: 'stream-335',
        name: 'STARZ',
        category: 'Entertainment' as ChannelCategory,
        logo: MdStar,
        color: 'from-yellow-500 to-black'
    },
    {
        id: 'stream-317',
        name: 'FX',
        category: 'Entertainment' as ChannelCategory,
        logo: MdTheaters,
        color: 'from-gray-900 to-gray-700'
    },
    {
        id: 'stream-338',
        name: 'TNT',
        category: 'Entertainment' as ChannelCategory,
        logo: MdLocalMovies,
        color: 'from-red-600 to-orange-500'
    },
    {
        id: 'stream-53',
        name: 'NBC',
        category: 'Entertainment' as ChannelCategory,
        logo: SiNbc,
        color: 'from-purple-600 via-red-500 to-yellow-500'
    },
    {
        id: 'stream-131',
        name: 'Telemundo',
        category: 'Entertainment' as ChannelCategory,
        logo: FaGlobeAmericas,
        color: 'from-red-600 to-blue-600'
    },

    // Kids
    {
        id: 'stream-339',
        name: 'Cartoon Network',
        category: 'Kids' as ChannelCategory,
        logo: MdChildCare,
        color: 'from-cyan-500 to-pink-500'
    },
    {
        id: 'stream-330',
        name: 'Nickelodeon',
        category: 'Kids' as ChannelCategory,
        logo: FaChild,
        color: 'from-orange-500 to-green-500'
    },
    {
        id: 'stream-652',
        name: 'Disney',
        category: 'Kids' as ChannelCategory,
        logo: GiCastle,
        color: 'from-blue-600 to-purple-600'
    },
    {
        id: 'stream-668',
        name: 'Universal Kids',
        category: 'Kids' as ChannelCategory,
        logo: MdPublic,
        color: 'from-blue-500 to-yellow-500'
    },

    // Music
    {
        id: 'stream-371',
        name: 'MTV',
        category: 'Music' as ChannelCategory,
        logo: IoMusicalNotes,
        color: 'from-yellow-400 to-pink-500'
    },
] as const;

/**
 * Get channels grouped by category
 */
export const getChannelsByCategory = (): ChannelsByCategory => {
    const categories: ChannelsByCategory = {};

    TV_CHANNELS.forEach(channel => {
        if (!categories[channel.category]) {
            categories[channel.category] = [];
        }
        categories[channel.category].push(channel);
    });

    return categories;
};

/**
 * Get all unique categories
 */
export const getCategories = (): ChannelCategory[] => {
    return [...new Set(TV_CHANNELS.map(channel => channel.category))];
};

/**
 * Get channel by ID
 */
export const getChannelById = (id: string): Channel | undefined => {
    return TV_CHANNELS.find(channel => channel.id === id);
};

/**
 * Filter channels by category
 */
export const filterChannelsByCategory = (category: ChannelCategory): Channel[] => {
    return TV_CHANNELS.filter(channel => channel.category === category);
};
